// 行事曆的 App 內通知（網頁開著時；docs/panel/09-voice-calendar.md §7.1、§7.2、§9.8、§9.9；VC1b / VC2，09 v0.9.1）。
// 推播目前只記不送（.111 沒有 APNs / FCM 金鑰；網頁也沒有接推播）→ 雲端在送推播的同一處替收件人存一份通知列，
// 網頁開著時讀 GET /v3/me/panel-notices（**以這份為準**）：
//   - C4 `panel.calendar_unacked`、C3 `panel.calendar_voice_added` / `_voice_changed`、`panel.calendar_ask_family`、
//     摘要 `panel.calendar_voice_summary` / `_unacked_summary`（合併由雲端做，網頁不再自己併）。
//   - 文案用雲端組好的 detail / ack_label（繁中）；沒有 detail 才用本機文案。
//   - ［收起］/ 點開 → POST /v3/me/panel-notices:read（其他裝置也跟著收起）；也記 localStorage（雲端讀不到時用）。
//   - C4 那一次已經確認了（GET calendar 看得到 done，且不是待核准按的）→ 不再顯示（通知列是紀錄，不會自己消失）。
// 備援：通知列表讀不到（503 / 斷網 / 舊雲端 404）時，C4 改從 GET calendar 推算（同雲端 §6.4 的條件），其他種類就等下一輪。
// 做法照 panelSos.ts：登入後（AppShell）每 60 秒輪詢（背景分頁暫停、回前景立刻讀）；GET calendar 讀「我是行事曆家人的面板」
//   （403 / 404 的這次登入不再讀），拿來判斷 C4 確認了沒與備援。
// 畫面：PanelCalNotices.tsx（頁面上方橫幅，點開導到行事曆那一件 / ask_family 開編輯預填）。
// D2 手機確認（docs/panel/10-phone-auth-scenes.md §10 推播清單）：同一條通知列也收 `me.confirm_key_added`（本人：帳號加了一支
//   可以幫面板確認的手機 → 帳號頁 V-11）、`panel.phone_denied`（主人：有人一直要求手機確認、家人都按了不是我 → 面板頁）、
//   `panel.settings`（主人：驗證方式 / 手機開門改了 → 面板頁）。雲端通知列的 data 沒帶手機名稱 / 面板名稱時：面板名稱用
//   面板清單補、手機名稱沒有就用雲端組好的 body。
// B3 新地點待核准（John 2026-10-26；05 V-09、09 §9.9 v0.9.5）：`panel.location`（只給主人）也收 → kind 'location'：
//   ［核准這個網路］選 V-09 選項（approve_modes：once / hours / permanent / remove；網頁不用解鎖）、［看常用地點］→ 面板頁；
//   雲端讀的時候算 resolved（核准 / 移除 / 回到常用地點）→ 那則不出、C4 不再給核准鈕；pendingPanels → 裝置清單 / 面板頁「待核准」徽章。
import { apiFetch } from '../../lib/api';
import { deviceName, isPanelDevice, type DeviceSummary } from './deviceModel';
import { approvePanelLocation, currentUserId, type LocationMode } from './panels';
import { ApiError } from '../../lib/api';
import {
  ackCalOccurrence, ackLabel, addDays, calErr, getPanelCalendar, nowWall, unackedVariant, wallDate, wallMinutes, wallTime,
  type CalendarView,
} from './panelCalendar';

export const CAL_NOTICE_POLL_MS = 60_000;
/** 面板清單（名字 / 在線 / 主人）每幾輪重讀一次 */
const PANEL_LIST_EVERY = 5;
/** 同一台 1 小時超過幾則就併成摘要（§7.2） */
export const NOTICE_MERGE_MAX = 3;

export type CalNoticeKind = 'unacked' | 'voice_added' | 'voice_changed' | 'ask_family' | 'voice_summary' | 'unacked_summary'
  | 'confirm_key_added' | 'phone_denied' | 'settings_changed' | 'location';

/** 手機確認相關（D2）：不是行事曆的那幾種 */
export const CONFIRM_NOTICE_KINDS: readonly CalNoticeKind[] = ['confirm_key_added', 'phone_denied', 'settings_changed'];
export const isConfirmNotice = (k: CalNoticeKind) => CONFIRM_NOTICE_KINDS.includes(k);

export interface CalNotice {
  /** 收起用的 id（同一件事同一個狀態 = 同一個 id） */
  id: string;
  kind: CalNoticeKind;
  panelId: string;
  /** 「客廳面板」/ caller_name（推播帶的這位家人看到的名字） */
  panelName: string;
  /** 發生 / 算出來的時間（ms），排序與 1 小時合併用 */
  at: number;
  eventId?: string;
  recurrenceId?: string;
  title?: string;
  /** 那一次的開始（面板當地牆上時間） */
  start?: string;
  allDay?: boolean;
  forWhom?: string;
  category?: string;
  /** C4：normal | offline | pending；ask_family：ask_edit | ask_delete | single_done */
  variant?: string;
  /** 待核准時按的時間 HH:mm */
  ackedTime?: string;
  /** voice_changed：delete | edit */
  op?: string;
  newStart?: string;
  newTitle?: string;
  /** ask_family：那一次原本的標題 / 開始時間（09 v0.9.2） */
  origTitle?: string;
  origStart?: string;
  /** C4 待核准給主人的［核准這個網路］：POST location:approve {mode}（09 §9.10；沒有 = 只能到面板頁處理） */
  approveMode?: LocationMode;
  /** 摘要：併了幾件 */
  count?: number;
  /** 主人（C4 待核准那則另有［核准這個網路］） */
  owner?: boolean;
  /** 推播帶來的點開內容（main 組好的字；有就照用） */
  detail?: string;
  /** 雲端組好的代確認按鈕字（「阿嬤說吃過了」）；有就照用 */
  ackLabelText?: string;
  /** 來自雲端通知列表（id 是雲端的 pn-…，收起要送 :read） */
  server?: boolean;
  read?: boolean;
  /** 雲端組好的推播內文（繁中）；新種類沒有可拼的欄位時照用 */
  body?: string;
  /** me.confirm_key_added：手機名稱（推播 data 有帶才有） */
  deviceName?: string;
  /** 新地點那則（B3）可以選的（V-09；來源不明只有 remove） */
  approveModes?: LocationMode[];
  /** 雲端讀的時候算的「已處理」（B3）：面板已不在那一次待核准 */
  resolved?: boolean;
}

// ── 推播資料 → 通知（之後真的收到推播 / 雲端通知列表時用同一個入口） ──

const s = (v: unknown) => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '');

const PUSH_KIND: Record<string, CalNoticeKind> = {
  'panel.calendar_unacked': 'unacked',
  'panel.calendar_voice_added': 'voice_added',
  'panel.calendar_voice_changed': 'voice_changed',
  'panel.calendar_ask_family': 'ask_family',
  'panel.calendar_voice_summary': 'voice_summary',
  'panel.calendar_unacked_summary': 'unacked_summary',
  'me.confirm_key_added': 'confirm_key_added',
  'panel.phone_denied': 'phone_denied',
  'panel.settings': 'settings_changed',
  'panel.location': 'location',
};

const APPROVE_MODES: readonly string[] = ['once', 'hours', 'permanent'];
const LOCATION_MODES: readonly string[] = ['once', 'hours', 'permanent', 'remove'];

/** 推播 data（§9.8：type, panel_device_id, caller_name, event_id, recurrence_id, occurrence_start, variant, detail, actions…）→ 通知；不是行事曆的回 null。
 *  通知列表那則的 extra（approve_* / owner…，§9.9）攤平進來一起看（推播 data 本來就在同一層）。 */
export function noticeFromPush(raw: Record<string, unknown>, at = Date.now()): CalNotice | null {
  const ex = raw.extra && typeof raw.extra === 'object' ? (raw.extra as Record<string, unknown>) : {};
  const data: Record<string, unknown> = { ...ex, ...raw };
  const kind = PUSH_KIND[s(data.type)];
  const panelId = s(data.panel_device_id);
  // 帳號的事（me.*）沒有面板；其他一律要有面板
  if (!kind || (!panelId && kind !== 'confirm_key_added')) return null;
  const eventId = s(data.event_id) || undefined;
  const rid = s(data.recurrence_id) || undefined;
  const count = Number(s(data.count)) || undefined;
  const actions = s(data.actions).split(',');
  // ［核准這個網路］：雲端只在主人那則、面板仍待核准、來源認得出來時給（actions 有 approve_network + approve_mode）；
  // 路徑一律自己用 panel_device_id 組（不照推播給的 approve_path 打任意網址）
  const mode = s(data.approve_mode);
  const resolved = data.resolved === true;
  const canApprove = actions.includes('approve_network') && !resolved;
  const approveMode = kind !== 'location' && canApprove && APPROVE_MODES.includes(mode) ? (mode as LocationMode) : undefined;
  const approveModes = kind === 'location' && canApprove
    ? (s(data.approve_modes).split(',').map((x) => x.trim()).filter((x) => LOCATION_MODES.includes(x)) as LocationMode[])
    : undefined;
  return {
    id: `push:${s(data.type)}:${panelId}:${eventId ?? ''}:${rid ?? ''}:${s(data.variant)}:${count ?? ''}`,
    kind, panelId, panelName: s(data.caller_name) || s(data.panel_name), at,
    eventId, recurrenceId: rid, start: s(data.occurrence_start) || s(data.start) || undefined, title: s(data.title) || undefined,
    forWhom: s(data.for_whom) || undefined, category: s(data.category) || undefined, variant: s(data.variant) || undefined,
    op: s(data.ask_op) || s(data.op) || undefined, newStart: s(data.new_start) || undefined, newTitle: s(data.new_title) || undefined,
    origTitle: s(data.orig_title) || undefined, origStart: s(data.orig_start) || undefined, approveMode,
    count, owner: s(data.owner) === '1' || actions.includes('approve_network'), detail: s(data.detail) || undefined,
    body: s(data.body) || undefined, deviceName: s(data.device_name) || undefined,
    approveModes, resolved: resolved || undefined,
  };
}

/** GET /v3/me/panel-notices 一則（§9.9：欄位同推播 data + id / at（unix 秒）/ read）→ 通知；不是行事曆的回 null */
export function noticeFromApi(raw: unknown): CalNotice | null {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const at = typeof o.at === 'number' ? o.at * 1000 : Date.now();
  const n = noticeFromPush(o, at);
  if (!n || typeof o.id !== 'string' || !o.id) return null;
  return { ...n, id: o.id, server: true, read: o.read === true, ackLabelText: s(o.ack_label) || undefined, body: s(o.body) || n.body };
}

/** 點通知要去哪裡：事件 → 行事曆那一次（ask_family 另開編輯並預填）；摘要 → 行事曆；沒有行事曆資訊 → 面板頁 */
export function noticeTarget(n: Pick<CalNotice, 'kind' | 'panelId' | 'eventId' | 'recurrenceId' | 'newStart' | 'newTitle' | 'op'>): string {
  if (n.kind === 'confirm_key_added') return '/account#my-phones';
  if (n.kind === 'phone_denied' || n.kind === 'settings_changed') return `/devices/${encodeURIComponent(n.panelId)}`;
  const base = `/devices/${encodeURIComponent(n.panelId)}/calendar`;
  if (!n.eventId) return base;
  const q = new URLSearchParams({ event: n.eventId });
  if (n.recurrenceId) q.set('rid', n.recurrenceId);
  if (n.kind === 'ask_family') {
    // ask_op = delete → 刪除確認；其他 → 那件的編輯畫面、預填長輩說的新時間 / 新標題（09 §9.10）
    if (n.op === 'delete') q.set('del', '1');
    else {
      q.set('edit', '1');
      if (n.newStart) q.set('new_start', n.newStart);
      if (n.newTitle) q.set('new_title', n.newTitle);
    }
  }
  return `${base}?${q.toString()}`;
}


// ── 從 GET calendar 算出來的（網頁開著、推播還沒送） ──

export interface PanelRef {
  id: string;
  name: string;
  online: boolean;
  owner: boolean;
}

/** 這一輪 now（面板當地牆上時間）換成 ms：用回應的 now（帶時差 ISO）；解析不了就用本機時間 */
function nowMs(view: CalendarView): number {
  const t = Date.parse(view.now);
  return Number.isFinite(t) ? t : Date.now();
}

/** C4：每一次沒確認的提醒一則（id 帶 variant：待核准 / 離線 → 換文案時再出現一次） */
export function deriveUnacked(view: CalendarView, p: PanelRef): CalNotice[] {
  const now = view.now;
  const nowM = wallMinutes(nowWall(now));
  const base = nowMs(view);
  return view.occurrences.flatMap((o): CalNotice[] => {
    const v = unackedVariant(o, now, p.online);
    if (!v) return [];
    const due = o.all_day ? `${wallDate(o.start)}T09:00` : o.start;
    const at = base - (nowM - wallMinutes(due) - 30) * 60000; // 準時 + 30 分鐘那一刻
    return [{
      id: `c4:${p.id}:${o.series_id ?? o.event_id}:${o.recurrence_id}:${v}`, kind: 'unacked', panelId: p.id, panelName: p.name, at,
      eventId: o.series_id ?? o.event_id, recurrenceId: o.recurrence_id, title: o.title, start: o.start, allDay: o.all_day,
      forWhom: o.for_whom, category: o.category, variant: v, owner: p.owner,
      ackedTime: v === 'pending' && o.ack ? o.ack.at.slice(11, 16) : undefined,
    }];
  });
}

/** 同一台：1 小時內 C3（added + changed）/ C4 超過 3 則 → 前 3 則照留、其餘併成一則摘要（§7.2、§9.8） */
export function mergeNotices(list: CalNotice[], now = Date.now()): CalNotice[] {
  const sorted = [...list].sort((a, b) => a.at - b.at);
  const out: CalNotice[] = [];
  const counts = new Map<string, number>();
  const extra = new Map<string, { n: number; panelName: string; at: number; ids: string[] }>();
  for (const n of sorted) {
    const group = n.kind === 'voice_added' || n.kind === 'voice_changed' ? 'c3' : n.kind === 'unacked' ? 'c4' : null;
    if (!group || now - n.at > 3600_000) {
      out.push(n);
      continue;
    }
    const k = `${group}#${n.panelId}`;
    const c = (counts.get(k) ?? 0) + 1;
    counts.set(k, c);
    if (c <= NOTICE_MERGE_MAX) {
      out.push(n);
      continue;
    }
    const e = extra.get(k) ?? { n: 0, panelName: n.panelName, at: n.at, ids: [] };
    e.n++;
    e.at = Math.max(e.at, n.at);
    e.ids.push(n.id);
    extra.set(k, e);
  }
  for (const [k, e] of extra) {
    const [group, panelId] = k.split('#');
    out.push({
      id: `sum:${k}:${e.ids.join('|')}`, kind: group === 'c3' ? 'voice_summary' : 'unacked_summary', panelId, panelName: e.panelName, at: e.at, count: e.n,
    });
  }
  return out.sort((a, b) => b.at - a.at);
}

/** 通知一則的主文：i18n key + 參數（鎖定畫面那種不帶標題的字網頁不用——網頁開著就是解鎖後） */
export function noticeText(n: CalNotice): { key: string; params: Record<string, unknown> } {
  const when = n.start ? (n.allDay ? wallDate(n.start).slice(5).replace('-', '/') : `${wallDate(n.start).slice(5).replace('-', '/')} ${wallTime(n.start)}`) : '';
  const time = n.start && !n.allDay ? wallTime(n.start) : '';
  const base = { panel: n.panelName, title: n.title ?? '', when, time, who: n.forWhom ?? '', n: n.count ?? 0, at: n.ackedTime ?? '' };
  switch (n.kind) {
    case 'unacked':
      if (n.variant === 'pending') return { key: 'panelCalNotice.unackedPending', params: base };
      if (n.variant === 'offline') return { key: 'panelCalNotice.unackedOffline', params: base };
      return { key: n.category === 'med' ? 'panelCalNotice.unackedMed' : 'panelCalNotice.unacked', params: base };
    case 'voice_added':
      return { key: n.forWhom ? 'panelCalNotice.voiceAddedFor' : 'panelCalNotice.voiceAdded', params: base };
    case 'voice_changed':
      if (n.op === 'delete') return { key: 'panelCalNotice.voiceDeleted', params: base };
      if (n.newStart) return { key: 'panelCalNotice.voiceMoved', params: { ...base, to: fmtWall(n.newStart) } };
      if (n.newTitle) return { key: 'panelCalNotice.voiceRenamed', params: { ...base, to: n.newTitle } };
      return { key: 'panelCalNotice.voiceChanged', params: base };
    case 'ask_family':
      if (n.variant === 'ask_delete' || n.op === 'delete') return { key: 'panelCalNotice.askDelete', params: base };
      if (n.variant === 'single_done') return { key: 'panelCalNotice.askSingleDone', params: base };
      if (n.newStart) return { key: 'panelCalNotice.askMove', params: { ...base, to: fmtWall(n.newStart) } };
      if (n.newTitle) return { key: 'panelCalNotice.askRename', params: { ...base, to: n.newTitle } };
      return { key: 'panelCalNotice.askEdit', params: base };
    case 'voice_summary':
      return { key: 'panelCalNotice.voiceSummary', params: base };
    case 'unacked_summary':
      return { key: 'panelCalNotice.unackedSummary', params: base };
    case 'confirm_key_added':
      // confirm.key_added（10 §17）；沒有手機名稱 → 照雲端的 body（noticeBodyFallback）
      return { key: 'confirm.key_added', params: { ...base, name: n.deviceName ?? '' } };
    case 'phone_denied':
      return { key: 'confirm.notice_phone_denied', params: base };
    case 'settings_changed':
      return { key: 'confirm.notice_settings', params: base };
    case 'location':
      return { key: 'panelCalNotice.locationTitle', params: base };
  }
}

/** 新種類要拼的欄位沒帶時，直接用雲端組好的 body（D2 收尾前的舊通知：me.confirm_key_added 的 data 沒帶手機名稱） */
export function noticeBodyFallback(n: CalNotice): string | undefined {
  if (n.kind === 'confirm_key_added' && !n.deviceName) return n.body;
  return undefined;
}

function fmtWall(w: string): string {
  const d = wallDate(w).slice(5).replace('-', '/');
  const t = wallTime(w);
  return t ? `${d} ${t}` : d;
}

/** C4 的［〇〇說吃過了］要用的 occurrence 欄位 */
export function noticeAckLabel(n: CalNotice): { key: string; params: Record<string, unknown> } {
  return ackLabel({ for_whom: n.forWhom, category: n.category ?? 'other' });
}

// ── 雲端通知列表 ──

export interface NoticeList {
  notices: CalNotice[];
  unread: number;
}

export async function listPanelNotices(limit = 100): Promise<NoticeList> {
  const raw = (await apiFetch<unknown>('/v3/me/panel-notices', { query: { limit: String(limit) } })).data;
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const list = Array.isArray(o.notices) ? o.notices.map(noticeFromApi).filter((x): x is CalNotice => x !== null) : [];
  return { notices: list, unread: typeof o.unread === 'number' ? o.unread : 0 };
}

export async function readPanelNotices(ids: string[]): Promise<void> {
  await apiFetch('/v3/me/panel-notices:read', { method: 'POST', body: { ids } });
}

/** C4 那一次已經確認了（done、不是待核准按的）→ 通知列那則不用再顯示；待核准文案那則（variant=pending）留著 */
export function unackedSettled(n: CalNotice, views: ReadonlyMap<string, CalendarView>): boolean {
  if (n.kind !== 'unacked' || n.variant === 'pending' || !n.eventId || !n.recurrenceId) return false;
  const v = views.get(n.panelId);
  const o = v?.occurrences.find((x) => (x.series_id ?? x.event_id) === n.eventId && x.recurrence_id === n.recurrenceId);
  return !!o?.ack && o.ack.result === 'done' && !o.ack.pending;
}

/** 「待核准」徽章（B3）：通知列裡還沒處理的新地點那則的面板（讀過 / 收起也算） */
export function pendingPanelIds(server: CalNotice[] | null): Set<string> {
  return new Set((server ?? []).filter((n) => n.kind === 'location' && !n.resolved).map((n) => n.panelId));
}

/** 這一輪要顯示的：雲端通知列表讀得到 → 用它（扣掉已確認的 C4、已讀的）；讀不到 → C4 從資料推算（1 小時超過 3 則併摘要） */
export function combineNotices(server: CalNotice[] | null, views: ReadonlyMap<string, CalendarView>, panels: PanelRef[], pushed: CalNotice[] = []): CalNotice[] {
  // 面板名稱：推播 data 只有行事曆 / 來電帶 panel_name → 其他種類用面板清單補
  const named = (n: CalNotice): CalNotice => (n.panelName || !n.panelId ? n : { ...n, panelName: panels.find((p) => p.id === n.panelId)?.name ?? '' });
  const extra = pushed.filter((p) => !(server ?? []).some((x) => x.id === p.id)).map(named);
  if (server) {
    // 新地點：已處理的不出；推播來的同一台新地點（通知列已經有）以通知列為準
    const loc = new Set(server.filter((n) => n.kind === 'location').map((n) => n.panelId));
    const ex = extra.filter((p) => !(p.kind === 'location' && loc.has(p.panelId)));
    return [...server.filter((n) => !n.read && !n.resolved && !unackedSettled(n, views)).map(named), ...ex].sort((a, b) => b.at - a.at);
  }
  const c4 = panels.flatMap((p) => {
    const v = views.get(p.id);
    return v ? deriveUnacked(v, p) : [];
  });
  return mergeNotices([...c4, ...extra]);
}

// ── 狀態與輪詢（同 panelSos.ts 的做法） ──

export interface CalNoticeState {
  list: CalNotice[];
  dismissed: ReadonlySet<string>;
  /** 正在代確認的通知 id */
  acking: string | null;
  /** 代確認失敗：id → 文案 key */
  ackErr: Record<string, string>;
  /** 推播（之後網頁有推播時）塞進來的（noticeFromPush） */
  pushed: CalNotice[];
  /** 雲端通知列表讀不到、正在用 C4 推算備援 */
  fallback: boolean;
  /** 正在核准網路的通知 id */
  approving: string | null;
  /** 核准結果：id → 文案 key（已核准 / 已經處理過了 / 失敗） */
  approveMsg: Record<string, string>;
  /** 新地點還沒處理的面板（B3；通知列讀不到時維持上一次） */
  pendingPanels: ReadonlySet<string>;
}

const LS_DISMISSED = 'ls.panelCalNotice.dismissed.';
const LS_MAX = 200;
const lsKey = () => LS_DISMISSED + (currentUserId() ?? 'anon');

function loadDismissed(): Set<string> {
  try {
    const raw = globalThis.localStorage?.getItem(lsKey());
    const arr: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr.filter((v): v is string => typeof v === 'string') : []);
  } catch {
    return new Set();
  }
}

function saveDismissed(d: ReadonlySet<string>): void {
  try {
    globalThis.localStorage?.setItem(lsKey(), JSON.stringify(Array.from(d).slice(-LS_MAX)));
  } catch {
    /* 私密視窗：這次開著的頁面仍記得 */
  }
}

const EMPTY: CalNoticeState = {
  list: [], dismissed: new Set(), acking: null, ackErr: {}, pushed: [], fallback: false, approving: null, approveMsg: {}, pendingPanels: new Set(),
};

let state: CalNoticeState = EMPTY;
const listeners = new Set<() => void>();
let timer: ReturnType<typeof setInterval> | null = null;
let inflight = false;
let generation = 0;
let round = 0;
let panels: PanelRef[] = [];
/** 這次登入不再讀的面板（403 / 404） */
const skip = new Set<string>();
const views = new Map<string, CalendarView>();
/** 最近一次讀到的雲端通知列表；null = 讀不到（用備援） */
let server: CalNotice[] | null = null;

function set(patch: Partial<CalNoticeState>) {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
}

const hidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

async function loadPanels(): Promise<PanelRef[]> {
  const res = await apiFetch<DeviceSummary[]>('/v3/devices', { query: { include: 'online' } });
  const list = Array.isArray(res.data) ? res.data : [];
  return list.filter(isPanelDevice).map((d) => ({ id: d.device_id, name: deviceName(d), online: d.panel?.online !== false, owner: d.role === 'owner' }));
}

function rebuild() {
  set({
    list: combineNotices(server, views, panels, state.pushed), fallback: server === null,
    pendingPanels: server === null ? state.pendingPanels : pendingPanelIds(server),
  });
}

async function pollPanel(p: PanelRef, gen: number): Promise<void> {
  try {
    // 範圍：昨天（剛過午夜的 C4）~ +14 天；第一次不帶範圍，先拿面板的今天
    const prev = views.get(p.id);
    const today = prev ? wallDate(nowWall(prev.now)) : '';
    const v = today ? await getPanelCalendar(p.id, addDays(today, -1), addDays(today, 14)) : await getPanelCalendar(p.id);
    if (gen !== generation) return;
    views.set(p.id, v);
  } catch (e) {
    const k = calErr(e).kind;
    if (k === 'forbidden' || k === 'notFound') {
      skip.add(p.id);
      views.delete(p.id);
    }
    /* 其他（斷網 / 5xx）：留著上一輪的 */
  }
}

async function poll(): Promise<void> {
  if (inflight || hidden()) return;
  inflight = true;
  const gen = generation;
  try {
    if (round % PANEL_LIST_EVERY === 0 || !panels.length) {
      try {
        panels = await loadPanels();
      } catch {
        /* 留著上一次的清單 */
      }
    }
    round++;
    if (gen !== generation) return;
    const [list] = await Promise.all([
      listPanelNotices().then((r) => r.notices, () => null),
      ...panels.filter((p) => !skip.has(p.id)).map((p) => pollPanel(p, gen)),
    ]);
    if (gen !== generation) return;
    server = list;
    rebuild();
  } finally {
    inflight = false;
  }
}

const onVisible = () => {
  if (!hidden()) void poll();
};

function resetModule() {
  panels = [];
  skip.clear();
  views.clear();
  server = null;
  round = 0;
}

export const panelCalNotice = {
  get: (): CalNoticeState => state,
  subscribe(l: () => void): () => void {
    listeners.add(l);
    return () => listeners.delete(l);
  },

  /** 登入後開始輪詢；回傳停止函式 */
  start(): () => void {
    panelCalNotice.stop();
    generation++;
    state = { ...EMPTY, dismissed: loadDismissed() };
    void poll();
    timer = setInterval(() => void poll(), CAL_NOTICE_POLL_MS);
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible);
    return () => panelCalNotice.stop();
  },

  stop(): void {
    if (timer) clearInterval(timer);
    timer = null;
    generation++;
    inflight = false;
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible);
    resetModule();
    state = EMPTY;
    listeners.forEach((l) => l());
  },

  refresh: poll,

  /** 推播收到一則（網頁之後接推播時用；App 開著時） */
  push(data: Record<string, unknown>): void {
    const n = noticeFromPush(data);
    if (!n || state.pushed.some((x) => x.id === n.id)) return;
    state = { ...state, pushed: [...state.pushed, n] };
    rebuild();
  },

  /** ［收起］/ 點開：記本機；雲端那則送 :read（失敗不管，本機已收起） */
  dismiss(id: string): void {
    const dismissed = new Set(state.dismissed).add(id);
    saveDismissed(dismissed);
    set({ dismissed });
    const n = state.list.find((x) => x.id === id);
    if (n?.server) void readPanelNotices([id]).catch(() => undefined);
  },

  /** C4 的［〇〇說吃過了］→ calendar:ack；成功後重讀（那一則因為已確認而消失）；422 = 已經過了時間 / 那次被刪 */
  async ack(n: CalNotice): Promise<void> {
    if (state.acking || !n.eventId || !n.recurrenceId) return;
    const gen = generation;
    const { [n.id]: _, ...rest } = state.ackErr;
    set({ acking: n.id, ackErr: rest });
    try {
      await ackCalOccurrence(n.panelId, { event_id: n.eventId, recurrence_id: n.recurrenceId });
      if (gen !== generation) return;
      set({ acking: null });
      if (n.server) void readPanelNotices([n.id]).catch(() => undefined);
      await poll();
    } catch (e) {
      if (gen !== generation) return;
      set({ acking: null, ackErr: { ...state.ackErr, [n.id]: `panelCal.err_${calErr(e).kind}` } });
    }
  },

  /** C4 待核准主人那則的［核准這個網路］→ POST /v3/panels/{id}/location:approve {mode}（09 §9.10）；
   *  成功 / 409（已經處理過）都標已讀、這則改顯示結果；其他錯誤顯示「沒有核准成功」可再按 */
  async approve(n: CalNotice): Promise<void> {
    if (state.approving || !n.approveMode) return;
    const gen = generation;
    set({ approving: n.id });
    let err: unknown = null;
    try {
      await approvePanelLocation(n.panelId, n.approveMode);
    } catch (e) {
      err = e;
    }
    if (gen !== generation) return;
    const key = approveResultKey(err);
    set({ approving: null, approveMsg: { ...state.approveMsg, [n.id]: key } });
    if (key !== 'panelCalNotice.approveFailed' && n.server) void readPanelNotices([n.id]).catch(() => undefined);
  },

  /** 新地點那則（B3）：選了 V-09 的選項 → location:approve（網頁不用解鎖，05 V-09）；成功 / 409 → 標已讀、馬上重讀（resolved → 收起、徽章拿掉） */
  async approveLocation(n: CalNotice, mode: LocationMode, hours?: number, ringDuring?: boolean): Promise<void> {
    if (state.approving || !n.approveModes?.includes(mode)) return;
    const gen = generation;
    set({ approving: n.id });
    let err: unknown = null;
    try {
      await approvePanelLocation(n.panelId, mode, hours, ringDuring);
    } catch (e) {
      err = e;
    }
    if (gen !== generation) return;
    const key = locationResultKey(mode, err);
    set({ approving: null, approveMsg: { ...state.approveMsg, [n.id]: key } });
    if (key !== 'panelCalNotice.approveFailed') {
      if (n.server) void readPanelNotices([n.id]).catch(() => undefined);
      void poll();
    }
  },
};

/** 新地點那則的結果文案 key：移除成功另一句 */
export function locationResultKey(mode: LocationMode, e: unknown): string {
  const k = approveResultKey(e);
  return k === 'panelCalNotice.approved' && mode === 'remove' ? 'panelCalNotice.locationRemoved' : k;
}

/** ［核准這個網路］的結果文案 key：成功 / 409 NO_PENDING（已經有人處理過）/ 其他失敗 */
export function approveResultKey(e: unknown): string {
  if (e === null) return 'panelCalNotice.approved';
  if (e instanceof ApiError && (e.code === 'NO_PENDING' || e.status === 409)) return 'panelCalNotice.approveAlready';
  return 'panelCalNotice.approveFailed';
}

/** 畫面要顯示的：沒收起的 */
export function visibleNotices(st: CalNoticeState): CalNotice[] {
  return st.list.filter((n) => !st.dismissed.has(n.id));
}

/** 測試用 */
export function __resetPanelCalNoticeForTest(): void {
  panelCalNotice.stop();
}
